/**
 * Which surface this deployment opens on (2026-08-07_LANDING_EXPERIENCE_RUNTIME).
 *
 * ── Three states, and the third is not "empty" ──────────────────────────
 *
 * `'chat'` and `'launcher'` are chosen; `null` means *inherit
 * `config/brands/<key>/brand.yaml`*, which is the state of every deployment
 * until someone touches this. A two-position control cannot express the third,
 * and collapsing it into `'chat'` would write a permanent override that
 * silently freezes a launcher-branded deployment on chat the next time its
 * brand file changes. So "use the build default" is a real, selectable
 * position — the same discipline the grace ceiling applies with a blank field,
 * spelled differently because a two-valued enum has no blank.
 *
 * ── Why the two surfaces are PREVIEW CARDS and the third is a row ───────
 *
 * This setting decides what every user sees when they open the product, and it
 * was a row of three text buttons. "Chat" and "App launcher" are the names of
 * two screens most operators have never deliberately compared, so the control
 * asked them to pick between two words. The cards draw each surface — a
 * conversations rail and a composer, versus a grid of app tiles — because the
 * question is visual and the answer should be too.
 *
 * The third state stays a full-width row BELOW them, not a third card. It is
 * not a third surface: it resolves to one of the two above, so a card would
 * render a preview identical to one of its neighbours and invite the reading
 * "there are three ways this can look". As a row it is unmissable and
 * unmistakably a different KIND of choice — defer, rather than decide.
 *
 * The previews are skeletons, deliberately, and share the geometry vocabulary
 * of `AppShellSkeleton`: a rail, a bar, a content area. They are diagrams of a
 * layout, not screenshots — a screenshot would go stale the first time either
 * surface is restyled, and nobody would notice.
 *
 * ── Why it renders on LAUNCHER and not on Platform settings ─────────────
 *
 * It began beside the grace ceiling, grouped by SCOPE (both are
 * deployment-wide). It now sits on the Launcher destination, grouped by
 * SUBJECT — which is the console's own principle. The grace ceiling and the
 * free-org limit are money and tenancy rules; "which surface is home" is only
 * an interesting question *because* the launcher exists, and the operator who
 * asks it is the one curating tiles. Splitting the two across destinations made
 * setup a two-page workflow held together by a warning and a link.
 *
 * ⚠ **Moving the control did NOT move the gate, and must not.**
 * `PUT /api/admin/platform-settings` is still
 * `requirePlatformCapability('settings', 'write')`, while the Launcher page
 * itself rides the blanket platform-admin grant and maps to `customers` for nav
 * visibility. So `canEdit` here comes from the **`settings`** area, threaded
 * separately in `apps/web/src/platformPages.tsx` — exactly as `canEditPricing`
 * is threaded into `CustomerDetail`, which renders a `pricing`-gated control on
 * a `customers` page for the same reason. The `settings` and `customers` grants
 * are genuinely distinct for the read-only tier: a `viewer`/`guest` seat holds
 * read on both and can retarget nothing. Deriving `canEdit` from the
 * surrounding page's `customers` grant would silently widen this control beyond
 * its `settings` gate. (Before 2026-09-03 the split also separated a `member`
 * seat — then `customers: 'write'`, `settings: 'read'` — which could curate a
 * customer's tiles but not retarget every user's home screen; member is now
 * full write, so the live boundary is viewer/guest.)
 *
 * ── Self-fetching, like BillingSettingsSection ──────────────────────────
 *
 * It owns its read and its write rather than taking a parent's `data`, because
 * its host is a screen with no interest in platform settings. Same shape
 * `BillingSettingsSection` uses on the page this came from.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useDraftState, CARD, SECTION_LABEL } from '@papan/core-ui/ui'
import { type LandingExperience } from '@papan/types'
import { budgetApi, type PlatformSettingsResponse } from '../../client/budget'

export function LandingSetting({ canEdit }: { canEdit: boolean }) {
  const [data, setData] = useState<PlatformSettingsResponse | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await budgetApi.getPlatformSettings())
    } catch {
      // Swallowed, and nothing renders. A `settings:read`-less caller cannot
      // reach this route at all while legitimately curating tiles below, so the
      // landing is a GUEST on this screen: its own failure must cost the
      // section, never the page. No error state and no retry button for the
      // same reason — both would put a platform-settings problem in front of
      // someone whose job here is curation.
      //
      // There was a `failed` flag beside this; it was redundant. On the initial
      // read `data` is already null, and on a failed RELOAD after a successful
      // save, keeping the last-known values is better than the section
      // vanishing under the operator mid-edit. Mutation-checking found it: the
      // flag could be deleted with every test still green.
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (!data) return null
  return <LandingEditor data={data} canEdit={canEdit} onSaved={load} />
}

/**
 * The selected-card treatment, as an OUTLINE rather than a ring.
 *
 * ⚠ `${CARD} … ring-2 ring-<accent>` does not work, and it fails silently. Both
 * that and `CARD`'s own `ring-1 ring-[color:var(--border-primary)]` are
 * arbitrary Tailwind values, so which one wins is decided by their order in the
 * generated stylesheet — NOT by the order they appear in the class attribute.
 * The first draft of this file looked correct in source and rendered a
 * selected card indistinguishable from an unselected one. `outline` sets a
 * different property, so it composes with `CARD` instead of racing it.
 */
const SELECTED = 'outline outline-2 outline-[color:var(--bill-primary)]'
/** Hover affordance for an unselected, editable card. Same non-collision rule:
 *  a border-colour change would fight `CARD`'s ring. */
const SELECTABLE = 'hover:outline hover:outline-1 hover:outline-[color:var(--border-secondary)]'

// ─── Skeleton previews ──────────────────────────────────────────────────
//
// Pure geometry, no data. Same vocabulary as `AppShellSkeleton` (rail, bar,
// content) so the diagram reads as "this product", and drawn from the theme's
// own surface variables so it looks right in all three themes rather than
// being a dark-mode drawing tinted light.

/**
 * Inline rather than `lucide-react`: `@papan/orgs` deliberately does not depend
 * on it, and pulling an icon package in for one glyph is the trade
 * `PlatformOverview` and `ExpensiveSessions` already declined.
 */
function CheckMark() {
  return (
    <svg
      viewBox="0 0 16 16" width={15} height={15} aria-hidden
      className="ml-auto shrink-0 text-[color:var(--bill-primary)]"
      fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  )
}

/** A skeleton bar. `w` is a Tailwind width class; height is fixed and tiny. */
function Bar({ w, className = '' }: { w: string; className?: string }) {
  return <div className={`h-1 rounded-full bg-[color:var(--border-secondary)] ${w} ${className}`} />
}

function PreviewFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="aspect-[16/10] w-full rounded-lg overflow-hidden ring-1 ring-[color:var(--border-primary)] bg-[color:var(--bg-primary)] flex gap-1.5 p-1.5">
      {/* The taskbar rail is in BOTH previews on purpose: it is what does not
          change between the two, so the difference the operator is being asked
          about is the only thing that differs on screen. */}
      <div className="w-3 shrink-0 rounded bg-[color:var(--bg-tertiary)] flex flex-col items-center gap-1 py-1">
        <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--border-secondary)]" />
        <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--border-secondary)]" />
        <div className="h-1.5 w-1.5 rounded-[2px] bg-[color:var(--border-secondary)]" />
      </div>
      {children}
    </div>
  )
}

/** Conversations rail on the left, greeting + composer centred on the stage. */
function ChatPreview() {
  return (
    <PreviewFrame>
      <div className="w-1/4 shrink-0 rounded bg-[color:var(--bg-secondary)] p-1 flex flex-col gap-1">
        <Bar w="w-3/4" />
        <Bar w="w-full" />
        <Bar w="w-2/3" />
        <Bar w="w-5/6" />
      </div>
      <div className="flex-1 rounded bg-[color:var(--bg-secondary)] flex flex-col items-center justify-center gap-2 px-2">
        <Bar w="w-1/2" className="h-1.5" />
        {/* The composer — the one shape that says "talk to something". */}
        <div className="h-4 w-full rounded-md bg-[color:var(--bg-tertiary)] ring-1 ring-[color:var(--border-secondary)]" />
      </div>
    </PreviewFrame>
  )
}

/** Heading, then a grid of app tiles. */
function LauncherPreview() {
  return (
    <PreviewFrame>
      <div className="flex-1 rounded bg-[color:var(--bg-secondary)] p-1.5">
        <Bar w="w-1/5" />
        <div className="mt-2 grid grid-cols-4 gap-x-2 gap-y-1.5">
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
            <div key={i} className="flex flex-col items-center gap-0.5">
              <div className="w-full aspect-square rounded-[3px] bg-[color:var(--bg-tertiary)] ring-1 ring-[color:var(--border-secondary)]" />
              <Bar w="w-3/4" className="h-0.5" />
            </div>
          ))}
        </div>
      </div>
    </PreviewFrame>
  )
}

const PREVIEW: Record<LandingExperience, () => React.JSX.Element> = {
  chat: ChatPreview,
  launcher: LauncherPreview,
}

// ─── The editor ─────────────────────────────────────────────────────────

function LandingEditor({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const stored = data.settings.landingExperience ?? null
  // Both `??`s guard this browser against an API instance that predates the
  // field — reachable for real, because the deploy does a ROLLING restart and
  // this bundle can be served by CF Pages before api-1 has come back. The
  // alternative is not a missing section: `data.advisory.x` on `undefined`
  // throws, which would take the whole Launcher page down with it.
  const brandLanding: LandingExperience = data.environment.brandLanding ?? 'chat'
  const curatedTenants = data.advisory?.tenantsWithShortcuts ?? null
  // A draft over `stored` (#897, kept across a phone/desktop layout swap):
  // until a card is clicked it IS `stored`, so a reload shows what persisted;
  // once clicked, no reload overwrites the click. Save / Discard drop it.
  //
  // This replaces a re-seed effect that ran on every `stored` CHANGE (guarded
  // against the mount run — an early click once raced it, see git history). A
  // live fallback has no effect to race: a click is never clobbered, and a
  // rejected save keeps the selection on screen, still dirty, as before.
  const [choice, setChoice, { clear: clearChoice, settle: settleChoice }] = useDraftState<LandingExperience | null>('platform:landing', stored)
  const [saving, setSaving] = useState(false)

  const dirty = choice !== stored
  // What users would GET under the pending selection — `null` resolves through
  // the brand, so the warning below is about the outcome rather than the click.
  const effective: LandingExperience = choice ?? brandLanding

  const label = (landing: LandingExperience) =>
    landing === 'launcher'
      ? t('launcher.admin.landing.launcher', { defaultValue: 'App launcher' })
      : t('launcher.admin.landing.chat', { defaultValue: 'Chat' })

  const blurb = (landing: LandingExperience) =>
    landing === 'launcher'
      ? t('launcher.admin.landing.launcherBlurb', {
          defaultValue: 'Opens on the curated grid of apps. Best when people come here to run something. The chat home is turned off; the AI stays inside each project.',
        })
      : t('launcher.admin.landing.chatBlurb', {
          defaultValue: 'Opens on the assistant, with recent conversations alongside. Best when people come here to ask something.',
        })

  const save = async () => {
    setSaving(true)
    try {
      // `null`, never `''` — the route validates against the closed union and
      // an empty string is a 400, not a clear.
      await budgetApi.setPlatformSettings({ landingExperience: choice })
      settleChoice()
      toast.success(t('launcher.admin.landing.saved', {
        defaultValue: 'Landing updated. Users see it on their next load.',
      }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const emptyLauncher = effective === 'launcher' && curatedTenants === 0

  return (
    <section className="mb-8">
      {/* ── Header: what this is, beside what it currently DOES ─────────
          The two facts belong on one line because an operator arriving here
          asks "what is set" before "what can I set". */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 mb-4">
        <div className="min-w-0">
          <h3 className={`${SECTION_LABEL} block`}>
            {t('launcher.admin.landing.title', { defaultValue: 'Landing surface' })}
          </h3>
          <p className="text-sm themed-text-muted mt-1 max-w-xl">
            {t('launcher.admin.landing.desc', {
              defaultValue: 'The screen this deployment opens on, for every tenant.',
            })}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* In force — the resolved value with its SOURCE. Without the source,
              an operator sees a live behaviour beside a control they never set
              and cannot tell configured from inherited. */}
          <div
            className="rounded-xl px-3 py-2 bg-[color:var(--bill-tint)] ring-1 ring-[color:var(--bill-ring)]"
            title={
              data.resolved.landingSource === 'setting'
                ? t('launcher.admin.landing.srcSetting', { defaultValue: 'Set here.' })
                : t('launcher.admin.landing.srcBrand', {
                    defaultValue: "Inherited from this deployment's brand configuration.",
                  })
            }
          >
            <div className="text-[10px] uppercase tracking-wide themed-text-muted">
              {t('launcher.admin.landing.inForce', { defaultValue: 'In force' })}
            </div>
            <div className="text-sm font-medium text-[color:var(--bill-primary)] leading-tight mt-0.5">
              {label(data.resolved.landingExperience)}
            </div>
          </div>

          <div
            className="rounded-xl px-3 py-2 themed-bg-secondary ring-1 ring-[color:var(--border-primary)]"
            title={t('launcher.admin.landing.curatedHint', {
              defaultValue: 'How many tenants have at least one app on their launcher.',
            })}
          >
            <div className="text-[10px] uppercase tracking-wide themed-text-muted">
              {t('launcher.admin.landing.curated', { defaultValue: 'Tenants with apps' })}
            </div>
            <div className="text-sm font-medium themed-text leading-tight mt-0.5 tabular-nums">
              {curatedTenants == null ? '—' : String(curatedTenants)}
            </div>
          </div>
        </div>
      </div>

      {/* ── The two surfaces ─────────────────────────────────────────── */}
      <div
        role="radiogroup"
        aria-label={t('launcher.admin.landing.title', { defaultValue: 'Landing surface' })}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {(['chat', 'launcher'] as const).map(kind => {
          const Preview = PREVIEW[kind]
          const active = choice === kind
          return (
            <button
              key={kind}
              role="radio"
              aria-checked={active}
              onClick={() => setChoice(kind)}
              disabled={saving || !canEdit}
              data-testid={`landing-option-${kind}`}
              className={`${CARD} relative text-left p-3 transition-all disabled:cursor-default ${
                active ? SELECTED : canEdit ? SELECTABLE : 'opacity-70'
              }`}
            >
              <Preview />

              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm font-semibold themed-text">{label(kind)}</span>
                {/* Which surface the build would give you. Shown on the card it
                    describes rather than only in the inherit row's label, so
                    "what happens if I defer" is answerable without reading. */}
                {brandLanding === kind && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full themed-bg-tertiary themed-text-muted">
                    {t('launcher.admin.landing.buildDefault', { defaultValue: 'Build default' })}
                  </span>
                )}
                {active && <CheckMark />}
              </div>
              <p className="text-xs themed-text-muted mt-1">{blurb(kind)}</p>
            </button>
          )
        })}

        {/* ── The third state ──────────────────────────────────────────
            A ROW, not a card: it is not a third surface, it resolves to one of
            the two above. Rendered as a card it would show a preview identical
            to a neighbour and read as "three ways this can look". */}
        <button
          role="radio"
          aria-checked={choice === null}
          onClick={() => setChoice(null)}
          disabled={saving || !canEdit}
          data-testid="landing-option-inherit"
          className={`${CARD} sm:col-span-2 flex items-start gap-3 text-left px-4 py-3 transition-all disabled:cursor-default ${
            choice === null ? SELECTED : canEdit ? SELECTABLE : 'opacity-70'
          }`}
        >
          <span
            className={`mt-0.5 h-4 w-4 shrink-0 rounded-full ring-1 flex items-center justify-center ${
              choice === null
                ? 'ring-[color:var(--bill-primary)]'
                : 'ring-[color:var(--border-secondary)]'
            }`}
            aria-hidden
          >
            {choice === null && <span className="h-2 w-2 rounded-full bg-[color:var(--bill-primary)]" />}
          </span>
          <span className="min-w-0">
            <span className="block text-sm themed-text">
              {/* Names the surface, because "use the build default" on its own
                  is a question. `brandLanding` comes from the server for
                  exactly this. */}
              {t('launcher.admin.landing.inherit', {
                defaultValue: 'Use the build default ({{landing}})',
                landing: label(brandLanding),
              })}
            </span>
            <span className="block text-xs themed-text-muted mt-0.5">
              {t('launcher.admin.landing.inheritMeans', {
                defaultValue: 'No override stored — the brand configuration decides, and keeps deciding if it changes.',
              })}
            </span>
          </span>
        </button>
      </div>

      {/* Which brand this deployment RESOLVED, and how (#428). The landing
          default above comes from that brand's config, so this is where an
          operator looks for it. `brandKey` alone cannot say whether anyone
          set BRAND_KEY — papan/brand.yaml is identical to the compiled
          default — so the server sends the resolution beside it. Guarded
          like `brandLanding` above: an API instance from before the field
          must not take the page down during a rolling restart. */}
      {data.environment.brandKey != null && (
        <p className="text-xs themed-text-muted mt-3">
          {data.environment.brandResolution === 'failed'
            ? t('launcher.admin.landing.brandFailed', {
                defaultValue: 'BRAND_KEY did not resolve — serving the built-in defaults as "{{key}}".',
                key: data.environment.brandKey,
              })
            : data.environment.brandResolution === 'unset'
              ? t('launcher.admin.landing.brandUnset', {
                  defaultValue: 'Brand: "{{key}}" — BRAND_KEY is not set, so this deployment serves the built-in defaults.',
                  key: data.environment.brandKey,
                })
              : t('launcher.admin.landing.brandConfigured', {
                  defaultValue: 'Brand: "{{key}}", set by BRAND_KEY.',
                  key: data.environment.brandKey,
                })}
          {/* WHY — the loader's own message, so an operator is not sent to a
              boot log the console cannot show them (round six, #428). */}
          {data.environment.brandResolution === 'failed' && data.environment.brandFailure && (
            <code className="block mt-1 text-[11px] themed-text-secondary break-words">{data.environment.brandFailure}</code>
          )}
        </p>
      )}

      {/* Non-blocking on purpose. Refusing the save until unrelated data
          exists is a setting that gets worked around with SQL — and an
          operator legitimately sets the landing first and curates after.
          No link any more: the tenant picker that fixes it is directly
          below, which is the whole reason this control moved here. */}
      {emptyLauncher && (
        <div className="rounded-xl ring-1 ring-amber-500/30 bg-amber-500/8 px-4 py-3 mt-4">
          <div className="text-amber-400 text-xs font-semibold">
            {t('launcher.admin.landing.emptyTitle', {
              defaultValue: 'No tenant has curated any apps yet',
            })}
          </div>
          <p className="themed-text-secondary text-xs mt-1">
            {t('launcher.admin.landing.emptyDesc', {
              defaultValue:
                'Users will land on an empty grid, which reads as a launcher that failed to load. Pick a tenant below and add their apps. Curating is not enough on its own: each app must also be SHARED with the people who need it, or they will see nothing even once the grid is filled.',
            })}
          </p>
        </div>
      )}

      {/* ── Footer: the timing note, and the commit ──────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        {/* Said unconditionally, not only after saving: an operator who flips
            this, looks at their own open tab and sees nothing change will flip
            it again. The value is read once per page load (and the public
            endpoint memoises briefly), so "next load" is the honest answer. */}
        <p className="themed-text-muted text-[11px] max-w-xl">
          {t('launcher.admin.landing.nextLoad', {
            defaultValue:
              'Takes effect on each user\'s next page load — open sessions keep the surface they started on.',
          })}
        </p>

        {canEdit && dirty && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={clearChoice}
              data-testid="landing-discard"
              disabled={saving}
              className="px-3 py-1.5 text-xs rounded-lg themed-text-secondary hover:themed-text disabled:opacity-50"
            >
              {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
            </button>
            <button
              onClick={save}
              // Its LABEL changes while saving, which e2e/CLAUDE.md names as
              // the case that requires a test id rather than a role matcher.
              data-testid="landing-save"
              disabled={saving}
              className="px-4 py-2 text-xs font-medium rounded-lg bg-gradient-to-r from-[#ddb8ff] to-[#0566d9] text-white hover:brightness-110 transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
            >
              {saving ? t('common:saving') : t('common:save')}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
